import { useState } from 'react';
import api from '../services/api';
import useNotification from './useNotification';

/**
 * Custom hook for Git repository operations
 * Provides functionality to connect, retrieve, and manage Git repositories
 * 
 * @returns {Object} Git repository hook methods and state
 */
const useGitRepository = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { showNotification } = useNotification();
  
  /**
   * Connect to a Git repository
   * 
   * @param {string} projectId - Project ID
   * @param {Object} repositoryData - Repository data
   * @returns {Promise<Object>} Connection result
   */
  const connectRepository = async (projectId, repositoryData) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Connecting repository with hook:', projectId);
      
      const response = await api.post(`/git-repositories/connect/${projectId}`, repositoryData);
      
      console.log('Repository connection response:', response);
      
      showNotification({
        message: 'Repository connected successfully',
        type: 'success'
      });
      
      return response.data;
    } catch (err) {
      console.error('Error in connectRepository hook:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to connect repository';
      setError(errorMsg);
      showNotification({
        message: errorMsg,
        type: 'error'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Get information about a connected repository
   * Handles 404 gracefully and prevents excessive API calls
   * 
   * @param {string} projectId - Project ID
   * @param {boolean} forceFresh - Whether to force a fresh check (ignore cache)
   * @returns {Promise<Object>} Repository information
   */
  const getRepositoryInfo = async (projectId, forceFresh = false) => {
    // First, check if we have cached connection data
    if (!forceFresh) {
      const connectionCacheKey = `git_repo_connection_${projectId}`;
      const cachedConnection = sessionStorage.getItem(connectionCacheKey);
      
      if (cachedConnection) {
        try {
          const parsedConnection = JSON.parse(cachedConnection);
          if (parsedConnection && parsedConnection.url) {
            console.log(`Using cached connection data for project ${projectId}`);
            return parsedConnection;
          }
        } catch (err) {
          console.error('Error parsing cached connection:', err);
          // Clear invalid storage
          sessionStorage.removeItem(connectionCacheKey);
        }
      }
    }
    
    // Track if we've already checked for this project and got a 404
    const cacheKey = `repo_check_${projectId}`;
    const hasChecked = sessionStorage.getItem(cacheKey);
    
    // If we've already checked and got a 404, don't check again unless forceFresh is true
    if (!forceFresh && hasChecked === 'not_found') {
      console.log(`Using client-side cached result: No repository connected for project ${projectId}`);
      setError('No repository connected');
      return null;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Use a timestamp query parameter for cache busting, but only if really needed
      // This reduces the number of unique URLs that can lead to unnecessary API calls
      let url = `/git-repositories/${projectId}`;
      if (forceFresh) {
        const timestamp = Date.now();
        url += `?_=${timestamp}`;
      }
      
      const response = await api.get(url);
      
      // If we get a successful response, clear any cached "not found" status
      sessionStorage.removeItem(cacheKey);
      
      // Cache the successful result to avoid future API calls
      const connectionData = response.data.data;
      if (connectionData && connectionData.url) {
        sessionStorage.setItem(`git_repo_connection_${projectId}`, JSON.stringify(connectionData));
      }
      
      return connectionData;
    } catch (err) {
      // Check for 404 - this is expected when no repository is connected
      if (err.response && err.response.status === 404) {
        // Cache this 404 result to prevent repeated calls
        sessionStorage.setItem(cacheKey, 'not_found');
        console.log(`Caching 404 result for project ${projectId}`);
        
        // Check if this is a cached response from the server
        const isCachedResponse = err.response.data && err.response.data.cached === true;
        if (isCachedResponse) {
          console.log('Server returned cached 404 response');
        }
        
        setError('No repository connected');
        return null; // Return null instead of throwing for 404
      }
      
      // For other errors, set error state and throw
      const errorMsg = err.response?.data?.message || err.message || 'Failed to get repository info';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Disconnect a repository from a project
   * 
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Disconnection result
   */
  const disconnectRepository = async (projectId) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.delete(`/git-repositories/${projectId}`);
      
      // Clear the cache entry so we don't retain the "not found" status
      const cacheKey = `repo_check_${projectId}`;
      sessionStorage.removeItem(cacheKey);
      console.log(`Cleared repository check cache after disconnect for project ${projectId}`);
      
      showNotification({
        message: 'Repository disconnected successfully',
        type: 'success'
      });
      
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to disconnect repository';
      setError(errorMsg);
      showNotification({
        message: errorMsg,
        type: 'error'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Search the repository for specific content
   * 
   * @param {string} projectId - Project ID
   * @param {string} query - Search query
   * @param {Array<string>} filePatterns - File patterns to search in
   * @returns {Promise<Object>} Search results
   */
  const searchRepository = async (projectId, query, filePatterns = []) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post(`/git-repositories/${projectId}/search`, {
        query,
        file_patterns: filePatterns
      });
      
      return response.data.data;
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to search repository';
      setError(errorMsg);
      showNotification({
        message: errorMsg,
        type: 'error'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Create a Claude Code workspace for a project
   * 
   * @param {string} projectId - Project ID
   * @param {boolean} withRepository - Whether to include the repository in the workspace
   * @returns {Promise<Object>} Workspace creation result
   */
  const createClaudeCodeWorkspace = async (projectId, withRepository = true) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post(`/git-repositories/${projectId}/workspace`, {
        with_repository: withRepository
      });
      
      showNotification({
        message: 'Claude Code workspace created successfully',
        type: 'success'
      });
      
      return response.data.data;
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to create Claude Code workspace';
      setError(errorMsg);
      showNotification({
        message: errorMsg,
        type: 'error'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  return {
    loading,
    error,
    connectRepository,
    getRepositoryInfo,
    disconnectRepository,
    searchRepository,
    createClaudeCodeWorkspace
  };
};

export default useGitRepository;